import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Code, Server, Cpu, Layers, CheckCircle, TrendingDown, BookOpen, ChevronRight, Sparkles, SplitSquareHorizontal } from 'lucide-react';

const TechRefreshProject = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const workflowSteps = [
    {
      step: '01',
      title: 'Starting Point',
      icon: Server,
      description: 'FWD Project Legacy Stack',
      details: [
        'JDK 8 → JDK 17',
        'Apache Camel 2.13 → Apache Camel 4.4.0',
        'Spring 4.x → Spring 6.2.3'
      ]
    },
    {
      step: '02',
      title: 'AI Tool Selection',
      icon: Cpu,
      description: 'Evaluated & Chosen AI Assistant',
      details: [
        'Evaluated: Tongyi Lingma',
        'Evaluated: AWS Q Developer',
        'Selected: AWS Q for superior accuracy'
      ]
    },
    {
      step: '03',
      title: 'TDD（Test-Driven Development）Process',
      icon: Layers,
      description: 'AI-Driven Development Cycle',
      details: [
        'AI modifies code',
        'Automated tests run',
        'Human reviews changes',
        'Tests verify quality'
      ]
    },
    {
      step: '04',
      title: 'Results',
      icon: CheckCircle,
      description: 'Outstanding Outcomes',
      details: [
        'Estimated: 358 person-days',
        'Actual: 178 person-days',
        '50% effort reduction',
        'Guidance documents created'
      ]
    }
  ];

  const kpiData = [
    {
      label: 'Estimated Effort',
      value: '358',
      unit: 'person-days',
      color: 'from-red-500 to-orange-500',
      icon: TrendingDown
    },
    {
      label: 'Actual with AI',
      value: '178',
      unit: 'person-days',
      color: 'from-green-500 to-emerald-500',
      icon: CheckCircle
    },
    {
      label: 'Efficiency Gain',
      value: '50%',
      unit: 'reduction',
      color: 'from-blue-500 to-cyan-500',
      icon: TrendingDown
    }
  ];

  const documentation = [
    {
      title: 'Best Practices',
      image: '/pic/tech_refresh/guide1.webp',
      description: 'Lessons learned and recommendations'
    },
    {
      title: 'Implementation Guide',
      image: '/pic/tech_refresh/guide2.webp',
      description: 'Detailed guide for future tech refresh projects'
    }
  ];

  return (
    <div className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 rounded-xl border border-yellow-500/30 p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-r from-yellow-500 to-amber-500 p-3 rounded-lg">
          <RefreshCw className="w-8 h-8 text-white" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-yellow-400">Tech Refresh Initiative</h3>
          <p className="text-sm text-gray-400">AI-Powered Infrastructure Modernization</p>
        </div>
      </div>

      {/* Introduction */}
      <div className="mb-8 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
        <p className="text-gray-300 leading-relaxed">
          Leveraging AI (AWS Q Developer) to modernize legacy technology stacks in the FWD project, achieving
          <span className="text-yellow-400 font-bold"> 50% effort reduction</span> while establishing reusable processes for future projects.
        </p>
      </div>

      {/* Workflow Steps - Visual Timeline */}
      <div className="mb-8">
        <h4 className="text-lg font-bold text-amber-400 mb-6 flex items-center gap-2">
          <Layers className="w-5 h-5" />
          AI-Driven Modernization Workflow
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {workflowSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative bg-white/5 rounded-lg p-5 border border-yellow-500/20 hover:border-yellow-500/40 transition-all"
              >
                {/* Step Number Badge */}
                <div className="absolute -top-3 -left-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-sm font-bold px-3 py-1 rounded-full shadow-lg">
                  {step.step}
                </div>

                <div className="flex items-start gap-3 mb-3">
                  <div className="bg-yellow-500/20 p-2 rounded-lg mt-1">
                    <Icon className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <h5 className="font-bold text-white mb-1">{step.title}</h5>
                    <p className="text-sm text-gray-400 mb-3">{step.description}</p>

                    <ul className="space-y-1">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                          <span className="text-yellow-400 mt-0.5">✓</span>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="mb-8">
        <h4 className="text-lg font-bold text-amber-400 mb-6 flex items-center gap-2">
          <TrendingDown className="w-5 h-5" />
          Key Performance Metrics
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {kpiData.map((kpi, index) => {
            const Icon = kpi.icon;
            return (
              <motion.div
                key={kpi.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-gradient-to-br ${kpi.color} rounded-xl p-6 text-center relative overflow-hidden`}
              >
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative">
                  <Icon className="w-8 h-8 mx-auto mb-3 text-white/80" />
                  <div className="text-4xl font-bold text-white mb-1">{kpi.value}</div>
                  <div className="text-lg text-white/90 mb-2">{kpi.unit}</div>
                  <div className="text-sm text-white/70">{kpi.label}</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* TDD Workflow Diagram */}
      <div className="mb-8">
        <h4 className="text-lg font-bold text-amber-400 mb-6 flex items-center gap-2">
          <Layers className="w-5 h-5" />
          Test-Driven Development Process
        </h4>

        <div className="bg-white/5 rounded-lg p-6 border border-yellow-500/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="aspect-video bg-gray-900/50 rounded-lg overflow-hidden border-2 border-yellow-500/30 cursor-pointer hover:border-yellow-500/50 transition-all"
                   onClick={() => setSelectedImage({ src: '/pic/tech_refresh/TDD.webp', title: 'TDD Process Workflow' })}>
                <img
                  src="/pic/tech_refresh/TDD.webp"
                  alt="TDD Workflow"
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-xs text-gray-400 mt-2 text-center">Click to enlarge</p>
            </div>

            <div className="space-y-3">
              <h5 className="font-bold text-yellow-400">Iterative Process</h5>

              <div className="flex items-start gap-3">
                <div className="bg-blue-500/20 p-2 rounded-lg">
                  <Code className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">AI Modifies Code</p>
                  <p className="text-xs text-gray-400">AWS Q Developer analyzes and updates legacy code</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-green-500/20 p-2 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Run Tests</p>
                  <p className="text-xs text-gray-400">Automated test suite validates changes</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-purple-500/20 p-2 rounded-lg">
                  <BookOpen className="w-4 h-4 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Human Review</p>
                  <p className="text-xs text-gray-400">Developer reviews and approves changes</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-amber-500/20 p-2 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Verify Quality</p>
                  <p className="text-xs text-gray-400">Final test run ensures quality standards</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documentation Gallery */}
      <div className="mb-6">
        <h4 className="text-lg font-bold text-amber-400 mb-6 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Guidance Documentation
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documentation.map((doc, index) => (
            <motion.div
              key={doc.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/5 rounded-lg overflow-hidden border border-yellow-500/20 hover:border-yellow-500/40 transition-all cursor-pointer"
              onClick={() => setSelectedImage({ src: doc.image, title: doc.title })}
            >
              <div className="aspect-video bg-gray-900/50 relative">
                <img
                  src={doc.image}
                  alt={doc.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-3 left-3 right-3">
                  <h5 className="text-sm font-bold text-white mb-1">{doc.title}</h5>
                  <p className="text-xs text-gray-300 line-clamp-2">{doc.description}</p>
                </div>
              </div>
              <div className="p-3">
                <div className="flex items-center text-yellow-400 text-xs">
                  <span>View Document</span>
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Key Takeaways */}
      <div className="mb-6 p-4 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 rounded-lg border border-yellow-500/30">
        <h5 className="font-bold text-yellow-400 mb-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          Key Achievements
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex items-start gap-2">
            <span className="text-green-400 mt-0.5">✓</span>
            <span className="text-gray-300"><strong className="text-white">50% efficiency gain:</strong> Reduced from 358 to 178 person-days</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-green-400 mt-0.5">✓</span>
            <span className="text-gray-300"><strong className="text-white">AI-optimized workflow:</strong> Established TDD best practices</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-green-400 mt-0.5">✓</span>
            <span className="text-gray-300"><strong className="text-white">Reusable process:</strong> Created guidance documents for future projects</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-green-400 mt-0.5">✓</span>
            <span className="text-gray-300"><strong className="text-white">Tool evaluation:</strong> AWS Q selected over Tongyi Lingma</span>
          </div>
        </div>
      </div>

      {/* Special Highlight: Microservices Strategy */}
      <div className="p-5 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg border-2 border-purple-500/30 relative overflow-hidden">
        {/* Sparkle decoration */}
        <div className="absolute top-2 right-2">
          <Sparkles className="w-6 h-6 text-purple-400 animate-pulse" />
        </div>

        <h5 className="font-bold text-purple-400 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          Strategic Breakthrough: Legacy to Microservices
        </h5>

        <div className="mb-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
          <p className="text-sm text-gray-300 leading-relaxed">
            <span className="text-purple-400 font-semibold">Key Discovery:</span> Through the FWD tech refresh process,
            we identified an <span className="text-white font-bold">AI-assisted strategy</span> to transform legacy monolithic
            core systems into <span className="text-pink-400 font-semibold">microservices architecture</span>.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <h6 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
              <Server className="w-4 h-4 text-red-400" />
              Legacy Monolith
            </h6>
            <ul className="space-y-1 text-xs text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span>Tight coupling between components</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span>Difficult to scale independently</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span>Single point of failure</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span>Slow deployment cycles</span>
              </li>
            </ul>
          </div>

          <div>
            <h6 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
              <SplitSquareHorizontal className="w-4 h-4 text-green-400" />
              AI-Driven Microservices
            </h6>
            <ul className="space-y-1 text-xs text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Loose coupling, independent services</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Individual scaling capabilities</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Improved fault isolation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Rapid deployment & iteration</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="p-3 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg border border-purple-500/30">
          <p className="text-xs text-purple-300 text-center">
            <span className="font-bold">💡 Strategic Value:</span> This AI-assisted transformation strategy can be applied to
            other legacy core projects, enabling <span className="text-white font-semibold">faster modernization</span> with
            <span className="text-white font-semibold">reduced risk</span> and <span className="text-white font-semibold">better maintainability</span>.
          </p>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-8"
          onClick={() => setSelectedImage(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative max-w-6xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-12 right-0 text-white hover:text-yellow-400 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <span className="text-2xl">✕</span>
            </button>
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-center text-white mt-4">{selectedImage.title}</p>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default TechRefreshProject;
